import React, { useState, useEffect, useCallback } from 'react'
import { FaChevronDown, FaStar, FaRegStar, FaTrash, FaEllipsisV } from 'react-icons/fa'
import { Box, Stack, Text, Button, Icon, Input, Checkbox, Menu, MenuItem, MenuSeparator, SimplePopover, SimpleModal, Loading, Tooltip } from '@/components/lib'
import api from '../../../api'
import utils from '../../../utils/utils'
import useStore from '../../../store'

const FILTER_TYPE = 'participants'

// The subset of participantsTableFilters a saved filter persists. `page` is
// deliberately excluded — a saved filter should never pin you to a page number.
// Exported so the "apply default on first load" logic (index.jsx) can share the
// exact same shape.
export const PARTICIPANT_FILTER_DEFAULTS = {
  sort: 'id',
  sortOrder: 'asc',
  searchTerm: '',
  selectedSiteId: null,
  groupIncludeIds: [],
  groupExcludeIds: [],
  groupNoneFilter: null,
  scheduleFilters: [],
  allocationFilters: [],
  joinFilters: [],
  showArchived: false
}

const FILTER_KEYS = Object.keys(PARTICIPANT_FILTER_DEFAULTS)

/** Pick just the persistable filter keys out of the live table-filter state. */
export function extractParticipantFilters (state) {
  const out = {}
  for (const k of FILTER_KEYS) {
    if (state?.[k] !== undefined) out[k] = state[k]
  }
  return out
}

/**
 * Turn a saved filter into a full table-filter state to apply. Starting from
 * defaults means keys absent from an older saved filter revert cleanly rather
 * than inheriting whatever was previously set; we always land on page 1.
 */
export function savedFilterToTableState (saved) {
  return { ...PARTICIPANT_FILTER_DEFAULTS, ...(saved?.filters || {}), page: 1 }
}

/** True when the table filters are still at their pristine defaults, i.e. the
 * user hasn't applied any search / filter / sort of their own yet. */
export function filtersAreDefault (state) {
  return FILTER_KEYS.every(k => {
    const def = PARTICIPANT_FILTER_DEFAULTS[k]
    const cur = state?.[k]
    if (Array.isArray(def)) return (cur?.length || 0) === 0
    if (typeof def === 'boolean') return !!cur === def
    if (def === null) return cur == null
    return (cur ?? def) === def
  })
}

// Trials whose default saved filter we've already auto-applied (or decided not
// to) this session. Module-level so it survives the List component remounting
// as the user tabs away and back — we only auto-apply on the first visit per
// trial, never clobbering filters the user has since set. Resets on page reload
// (a fresh load legitimately re-applies the default view).
const defaultAppliedTrials = new Set()

export default function SavedFilters () {
  const { trial, participantsTableFilters, setParticipantsTableFilters } = useStore()
  const trialId = trial?._id

  const [savedFilters, setSavedFilters] = useState([])
  const [loading, setLoading] = useState(false)

  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState(null) // null = creating a new one
  const [name, setName] = useState('')
  const [isDefault, setIsDefault] = useState(false)
  const [saving, setSaving] = useState(false)

  const load = useCallback(() => {
    if (!trialId) return
    setLoading(true)
    api.trials.getSavedFilters(trialId, FILTER_TYPE, ({ savedFilters }) => {
      setLoading(false)
      setSavedFilters(savedFilters || [])
      maybeApplyDefault(trialId, savedFilters || [])
    }, err => {
      setLoading(false)
      utils.toast('error', 'Unable to load saved filters', err.message)
    })
  }, [trialId])

  useEffect(load, [load])

  // On the first visit to a trial's participant table this session, apply the
  // user's default saved filter — but only if they haven't already changed the
  // filters (reading the freshest store state, since this runs in an async
  // callback), and only if they're still on this trial.
  function maybeApplyDefault (loadedTrialId, filters) {
    if (defaultAppliedTrials.has(loadedTrialId)) return
    // Don't mark the trial handled until we know it's still the active one:
    // if the user switched trials while this request was in flight, we want to
    // remain eligible to auto-apply when they navigate back.
    const state = useStore.getState()
    if (state.trial?._id !== loadedTrialId) return
    defaultAppliedTrials.add(loadedTrialId)
    const def = filters.find(f => f.isDefault)
    if (!def) return
    if (!filtersAreDefault(state.participantsTableFilters)) return
    setParticipantsTableFilters(savedFilterToTableState(def))
  }

  function applyFilter (saved) {
    setParticipantsTableFilters(savedFilterToTableState(saved))
  }

  function openCreate () {
    setEditing(null)
    setName('')
    setIsDefault(false)
    setModalOpen(true)
  }

  function openRename (saved) {
    setEditing(saved)
    setName(saved.name)
    setIsDefault(!!saved.isDefault)
    setModalOpen(true)
  }

  function submitModal () {
    const trimmed = name.trim()
    if (!trimmed) {
      utils.toast('error', 'Please enter a name')
      return
    }
    setSaving(true)
    if (editing) {
      // Editing name / default only — the stored filters are left untouched.
      api.trials.updateSavedFilter(trialId, editing._id, { name: trimmed, isDefault }, () => {
        setSaving(false)
        setModalOpen(false)
        load()
      }, err => {
        setSaving(false)
        utils.toast('error', 'Unable to update saved filter', err.message)
      })
    } else {
      api.trials.createSavedFilter(trialId, {
        name: trimmed,
        filterType: FILTER_TYPE,
        filters: extractParticipantFilters(participantsTableFilters),
        isDefault
      }, () => {
        setSaving(false)
        setModalOpen(false)
        utils.toast('success', 'Filter saved')
        load()
      }, err => {
        setSaving(false)
        utils.toast('error', 'Unable to save filter', err.message)
      })
    }
  }

  function overwriteWithCurrent (saved) {
    api.trials.updateSavedFilter(trialId, saved._id, {
      filters: extractParticipantFilters(participantsTableFilters)
    }, () => {
      utils.toast('success', 'Saved filter updated to your current filters')
      load()
    }, err => utils.toast('error', 'Unable to update saved filter', err.message))
  }

  function toggleDefault (saved) {
    api.trials.updateSavedFilter(trialId, saved._id, { isDefault: !saved.isDefault }, () => {
      load()
    }, err => utils.toast('error', 'Unable to update saved filter', err.message))
  }

  function remove (saved) {
    if (!window.confirm(`Delete saved filter "${saved.name}"?`)) return
    api.trials.deleteSavedFilter(trialId, saved._id, () => {
      utils.toast('success', 'Saved filter deleted')
      load()
    }, err => utils.toast('error', 'Unable to delete saved filter', err.message))
  }

  return (
    <>
      <SimplePopover
        closeOnBlur
        contentProps={{ w: { base: '90vw', md: 340 }, maxW: '95vw' }}
        header='Saved filters'
        trigger={
          <Button size='xs' variant='outline'>
            Saved filters <Icon as={FaChevronDown} />
          </Button>
        }
        body={
          <Stack gap={2}>
            {loading
              ? <Box textAlign='center'><Loading /></Box>
              : savedFilters.length === 0
                ? (
                  <Text fontSize='sm' color='fg.muted'>
                    You haven't saved any filters yet. Set up the search, filters and sorting you want, then save them below.
                  </Text>
                  )
                : (
                  <Stack gap={1}>
                    {savedFilters.map(f => (
                      <Stack key={f._id} direction='row' alignItems='center' gap={1}>
                        <Button
                          size='xs'
                          variant='ghost'
                          justifyContent='start'
                          flex='1'
                          minW={0}
                          onClick={() => applyFilter(f)}
                        >
                          {f.isDefault
                            ? <Tooltip label='Applied by default'><Box display='inline-flex'><Icon as={FaStar} color='yellow.500' /></Box></Tooltip>
                            : <Icon as={FaRegStar} color='fg.muted' />}
                          <Text as='span' ml={2} overflow='hidden' textOverflow='ellipsis' whiteSpace='nowrap'>{f.name}</Text>
                        </Button>
                        <Menu
                          trigger={<Button size='xs' variant='ghost'><Icon as={FaEllipsisV} /></Button>}
                        >
                          <MenuItem value='default' onClick={() => toggleDefault(f)}>
                            {f.isDefault ? 'Remove as default' : 'Set as default'}
                          </MenuItem>
                          <MenuItem value='overwrite' onClick={() => overwriteWithCurrent(f)}>
                            Update to current filters
                          </MenuItem>
                          <MenuItem value='rename' onClick={() => openRename(f)}>
                            Rename…
                          </MenuItem>
                          <MenuSeparator />
                          <MenuItem value='delete' onClick={() => remove(f)}>
                            <Stack direction='row' alignItems='center' gap={2}><Icon as={FaTrash} /> Delete</Stack>
                          </MenuItem>
                        </Menu>
                      </Stack>
                    ))}
                  </Stack>
                  )}
            <hr />
            <Button size='xs' variant='outline' onClick={openCreate}>
              Save current filters…
            </Button>
          </Stack>
        }
      />

      <SimpleModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        header={<Text fontWeight='semibold'>{editing ? 'Edit saved filter' : 'Save current filters'}</Text>}
        body={
          <Stack gap={3}>
            <Box>
              <Text fontSize='sm' mb={1}>Name</Text>
              <Input
                size='sm'
                autoFocus
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder='e.g. Excludes control group'
                onKeyDown={e => { if (e.key === 'Enter') submitModal() }}
              />
            </Box>
            <Checkbox isChecked={isDefault} onChange={e => setIsDefault(!!e.checked)}>
              Apply this filter by default when I open the participants table
            </Checkbox>
            {!editing && (
              <Text fontSize='xs' color='fg.muted'>
                This saves your current search, filters and sorting. Saved filters are private to you.
              </Text>
            )}
          </Stack>
        }
        footer={
          <Stack direction='row' justifyContent='end' gap={2}>
            <Button size='sm' variant='ghost' onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button size='sm' colorPalette='primary' onClick={submitModal} isLoading={saving}>Save</Button>
          </Stack>
        }
      />
    </>
  )
}
